import React from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skull, MessageSquare } from 'lucide-react';

const HallOfShameTab = ({ topShamer, shameLeaderboard, shameFeed, shameSort, setShameSort, shamePost, renderMedia, period }) => (
  <div className="space-y-6">
    {topShamer && (
      <Card className="p-5 bg-gradient-to-r from-gray-800 to-gray-950 text-white rounded-2xl" data-testid="top-shamer-banner">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-xl"><Skull className="w-8 h-8" /></div>
          <div>
            <p className="text-sm opacity-90">Wall of Shame MVP</p>
            <p className="text-xl font-bold">{topShamer.user?.name || 'Unknown'}</p>
            <p className="text-sm opacity-80">@{topShamer.user?.username} · {topShamer.total_shames} shames · {topShamer.post_count} posts</p>
          </div>
        </div>
      </Card>
    )}

    <Card className="p-4 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700" data-testid="shame-how-to">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-xl"><Skull className="w-5 h-5 text-gray-600 dark:text-gray-400" /></div>
        <div>
          <h3 className="font-bold text-sm text-gray-900 dark:text-white">How to nominate</h3>
          <p className="text-xs text-gray-500">Include <span className="font-bold text-gray-700 dark:text-gray-300">#hallofshame</span> in your post to put it on the wall.</p>
        </div>
      </div>
    </Card>

    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500 font-medium">Sort by:</span>
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full p-1">
        <button onClick={() => setShameSort('recent')} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${shameSort === 'recent' ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm' : 'text-gray-500'}`} data-testid="shame-sort-recent">Newest</button>
        <button onClick={() => setShameSort('top')} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${shameSort === 'top' ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm' : 'text-gray-500'}`} data-testid="shame-sort-top">Most Shamed</button>
      </div>
    </div>

    <Card className="rounded-2xl overflow-hidden" data-testid="shame-leaderboard">
      <div className="p-5 bg-gradient-to-r from-gray-800 to-gray-950 text-white">
        <h3 className="text-lg font-bold flex items-center gap-2"><Skull className="w-5 h-5 text-red-400" /> Top 10 Hall of Shame · {period === 'month' ? 'This Month' : 'All Time'}</h3>
      </div>
      <div className="divide-y dark:divide-gray-700">
        {shameLeaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="shame-leaderboard-empty"><Skull className="w-10 h-10 mx-auto mb-2 text-gray-300" /><p>No shame yet. Post with #hallofshame to get started!</p></div>
        ) : shameLeaderboard.map((post) => (
          <div key={post.post_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition" data-testid={`shame-rank-${post.rank}`}>
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${post.rank === 1 ? 'bg-red-500 text-white' : post.rank === 2 ? 'bg-red-400 text-white' : post.rank === 3 ? 'bg-red-300 text-red-900' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>{post.rank}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{post.author?.name || 'Unknown'}</span>
                  <span className="text-xs text-gray-500">@{post.author?.username}</span>
                </div>
                <p className="text-gray-800 dark:text-gray-200 text-sm mb-1">{post.content}</p>
                {renderMedia(post)}
              </div>
              <button onClick={() => shamePost(post.post_id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold transition-all ${post.user_shamed ? 'bg-red-600 text-white shadow-lg' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-red-100'}`}
                data-testid={`shame-btn-${post.post_id}`}>
                <Skull className="w-4 h-4" /><span>{post.shame_count || 0}</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>

    {shameFeed.length > 0 && (
      <Card className="rounded-2xl overflow-hidden" data-testid="shame-feed">
        <div className="p-4 border-b dark:border-gray-700"><h3 className="font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Latest Shamed Posts</h3></div>
        <div className="divide-y dark:divide-gray-700">
          {shameFeed.map((post) => (
            <div key={post.post_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition" data-testid={`shame-feed-${post.post_id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="w-9 h-9 flex-shrink-0"><AvatarImage src={post.author?.avatar_url} /><AvatarFallback>{post.author?.name?.charAt(0) || '?'}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{post.author?.name}</span>
                    <span className="text-xs text-gray-500">@{post.author?.username}</span>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200 text-sm">{post.content}</p>
                  {renderMedia(post)}
                </div>
                <button onClick={() => shamePost(post.post_id)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${post.user_shamed ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600'}`}
                  data-testid={`shame-feed-btn-${post.post_id}`}>
                  <Skull className="w-3.5 h-3.5" /> {post.shame_count || 0}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    )}
  </div>
);

export default HallOfShameTab;
